import React from "react";
import {observer} from "mobx-react";
import {useNavigate} from "react-router-dom";
import {ModalContent} from "views/components/modals/common";
import {Button, FormControl, Modal, Stack, Typography} from "@mui/material";
import {useViewController} from "data/services/locator";
import {IModalSecondaryRegistrationController} from "views/components/modals/modal_secondary_registration/modal_secondary_registration.controller";
import {Bindings} from "data/constants/bindings";
import gameLogo from "assets/img/game_logo.png";
import styled from "@emotion/styled";
import {Checkbox, Input, Label} from "views/components/form";
import {Exist} from "views/components/exist/exist.component";
import DOMPurify from "dompurify";

const LogoWrapper = styled.div`
	width: 100%;
	height: 80px;
	display: flex;
	justify-content: center;
	align-items: center;
	img {
		height: 100%;
	}
`;

const Form = styled.form`
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;
	gap: 16px;
	width: 100%;

	button {
		max-width: unset;
	}
`;

const Title = styled.h4`
	color: #ffffff;
	text-align: center;
	font-size: 20px;
	font-style: normal;
	font-weight: 600;
	line-height: 140%; /* 28px */
`;

const StyledLabel = styled(Label)`
	a {
		text-decoration: underline;
	}
`;

const Disclaimer = styled(Title)`
	font-size: 12px;
	font-weight: 400;

	a {
		text-decoration: underline;
		text-underline-offset: 2px;
	}
`;

export const ModalSecondaryRegistration: React.FC = observer(() => {
	const navigate = useNavigate();
	const {
		i18n,
		error,
		displayName,
		isOpen,
		isDisabled,
		handleFormSubmit,
		handleInputChange,
		validationMessage,
	} = useViewController<IModalSecondaryRegistrationController>(
		Bindings.ModalSecondaryRegistrationController,
		{navigate}
	);

	const helperText = i18n.t("modal.registration.displayName_helper", "Max 15 characters");
	return (
		<Modal open={isOpen}>
			<ModalContent>
				<LogoWrapper>
					<img src={gameLogo} alt="" role={"presentation"} />
				</LogoWrapper>

				<Form onSubmit={handleFormSubmit}>
					<Exist when={Boolean(error)}>
						<Typography variant="h5" color="red">
							{error}
						</Typography>
					</Exist>
					<Title>{i18n.t("modal.registration.title", "Name your Team")}</Title>
					<FormControl fullWidth>
						<Input
							variant="filled"
							label={i18n.t("modal.registration.displayName_label", "Display Name")}
							name="displayName"
							type="text"
							value={displayName}
							onChange={handleInputChange}
							placeholder={i18n.t(
								"modal.registration.displayName_placeholder",
								"Display Name"
							)}
							required={true}
							error={!!validationMessage}
							helperText={validationMessage ? validationMessage : helperText}
						/>
					</FormControl>
					<Stack
						direction="row"
						justifyContent="flex-start"
						alignItems="center"
						width="100%"
						gap={2}>
						<Checkbox defaultChecked={false} required={true} name="terms" id="terms" />

						<StyledLabel
							htmlFor="terms"
							color="#FFFFFF"
							dangerouslySetInnerHTML={{
								/* eslint-disable-next-line react/no-danger */
								__html: DOMPurify.sanitize(
									i18n.t(
										"modal.registration.check_box_label",
										`Accept <a href="https://mlb.com/apps/spinball/official-rules">Terms and Conditions</a>`
									)
								),
							}}
						/>
					</Stack>

					<Button
						disabled={isDisabled || !!validationMessage}
						type="submit"
						variant="contained"
						color="secondary"
						fullWidth>
						{i18n.t("modal.registration.action", "Continue")}
					</Button>

					<Disclaimer
						dangerouslySetInnerHTML={{
							/* eslint-disable-next-line react/no-danger */
							__html: DOMPurify.sanitize(
								i18n.t(
									"modal.registration.disclaimer",
									`NPN. US and CAN, 18+. Ends 11:59pm ET on 9/28. Restrictions apply; see <a href="https://mlb.com/apps/spinball/official-rules">Official Rules</a>.`
								)
							),
						}}
					/>
				</Form>
			</ModalContent>
		</Modal>
	);
});
